import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { IconComponent } from '../../../../../../../../shared/components/ui/icon/icon.component';
import { TranslatePipe } from '../../../../../../../../shared/pipes/translate/translate.pipe';
import { ChecklistsQuestion } from '../../interfaces/checklists-question.interface';
import { EditorComponent } from 'src/app/shared/components/editor/editor.component';
import { FileSelectMultiComponent } from 'src/app/shared/components/file-select-multi/file-select-multi.component';
import { MailingAttachmentsUploadComponent } from '../../../form-mailing-list/mailing-attachments-upload/mailing-attachments-upload.component';
import { ChecklistAnswerClassType } from 'src/models/ts/checklist-answer-class-type';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { AnswerAttachment, ChecklistApiService } from 'src/app/api/bizzmine/checklist/checklist.service';
import { isQuestionAnswered } from '../../functions/question-valid';
import { toNumber } from 'lodash';
import { ChecklistAnswerTypesComponent } from '../answers/answers/answers.component';
import { ResultClassValue } from 'src/models/ts/result-class-value';
import { SafePipe } from 'safe-pipe';
import { PopupRef } from '@progress/kendo-angular-popup/models/popup-ref';
import { PopupService } from '@progress/kendo-angular-popup';
import { CommonModule } from '@angular/common';
import { DownloadService } from 'src/app/core/services/download/download.service';

const HIDE_POPUP_DELAY_TIME = 100; //ms


@Component({
  selector: 'bizz-checklist-question',
  standalone: true,
  imports: [
    IconComponent,
    TranslatePipe,
    EditorComponent,
    MailingAttachmentsUploadComponent,
    ChecklistAnswerTypesComponent,
    SafePipe,
    CommonModule
  ],
  templateUrl: './question.component.html',
  styleUrl: './question.component.scss'
})
export class ChecklistQuestionComponent  {
  @Input() public questionNumber: number;
  @Input() public isMobileView: boolean = false;
  @Input() public isCurrentQuestion: boolean = false;
  @Input() public isLoading: boolean = false;
  @Input() public isDarkTheme: boolean = false;
  @Input() public reportControl: boolean = false;
  @Output() public questionChange = new EventEmitter<ChecklistsQuestion>;
  @Output() public saveQuestion = new EventEmitter<ChecklistsQuestion>;
  @Output() public collapseQuestion = new EventEmitter<boolean>;
  //@Input({ required: true }) public question: ChecklistsQuestion;
  public isLocked = input.required<boolean>();
  public isRequired = input.required<boolean>();
  @Input() public formId: string;
  @Input() public formFieldId: number;
  @Input() public question: ChecklistsQuestion;
  @Input() public set data(data:ChecklistsQuestion){
    this.question = data;
    if (this.question.UserAnswer.Attachments) {
      this.attachments = this.question.UserAnswer.Attachments.map((attachment: AnswerAttachment) => ({ id: attachment.Id, size: attachment.Size, fileName: attachment.FileName }))
      if(this.attachments.length) this.toggleAttachments();
    }

    if (this.question.UserAnswer.Remarks && this.question.UserAnswer.Remarks.length >0) {
      this.toggleRemarks();
    }

    if(this.question.UserAnswer.ResultClassValueID){
      this.question.UserAnswer.Answer = this.question.ResultClass.ResultClassValues.Values.find((resultClassValue: ResultClassValue)=> resultClassValue.ID == this.question.UserAnswer.ResultClassValueID);
    }

    if (this.question.ResultClass.ResultClassType == ChecklistAnswerClassType.Numeric && this.question.UserAnswer.Value) {
      this.question.UserAnswer.Value = toNumber(this.question.UserAnswer.Value);
    }
  }

  public showRemarks: boolean = false;
  public showAttachments: boolean = false;

  public attachments: { id: number, fileName: string, size: number }[] = [];
  public mediaIds: number[] = [];
  public ChecklistAnswerClassType = ChecklistAnswerClassType;

  @ViewChild('multiSelectInput') public inputElement: MultiSelectComponent;
  @ViewChild('attachmentsUploadComponentFileSelect', { static: true }) public fileSelectMultiComponent: FileSelectMultiComponent;

  @ViewChild('readmorePopupTemplateRef', { static: true }) public readmorePopupTemplateRef: TemplateRef<any>;

  @ViewChild('anchor', { read: ElementRef }) public anchorEl: ElementRef;
  private readmorePopup: PopupRef | null = null;
  private anchorHovered = false;
  private popupHovered = false;
  private checklistApiService = inject(ChecklistApiService);
  private downloadService = inject(DownloadService);
  public constructor(private popupService: PopupService) {
  }

  public ngOnInit(): void {
    if (this.question.AttachmentAndRemarksRequiredSettings) {
      // backend could overwrite the required settings on each value (template driven) we iterate over all values and set the required settings if needed
      if (this.question.AttachmentAndRemarksRequiredSettings.IsAttachmentRequired) {
        this.question.ResultClass.ResultClassValues.Values.forEach((value: ResultClassValue) => {
          value.AttachmentRequired = true;
        });
      }
      if (this.question.AttachmentAndRemarksRequiredSettings.IsRemarkRequired) {
        this.question.ResultClass.ResultClassValues.Values.forEach((value: ResultClassValue) => {
          value.RemarksRequired = true;
        });
      }
    }

    if (this.question.UserAnswer.Attachments) {
      this.attachments = this.question.UserAnswer.Attachments.map((attachment: AnswerAttachment) => ({
        id: attachment.Id,
        size: attachment.Size,
        fileName: attachment.FileName
      }));
      this.mediaIds = this.attachments.map(a => a.id);
      if (this.attachments.length) this.toggleAttachments();
    }

    if (this.question.UserAnswer.Remarks && this.question.UserAnswer.Remarks.length > 0) {
      this.toggleRemarks();
    }

    if (this.question.UserAnswer.ResultClassValueID) {
      this.question.UserAnswer.Answer = this.question.ResultClass.ResultClassValues.Values.find((resultClassValue: ResultClassValue) => resultClassValue.ID == this.question.UserAnswer.ResultClassValueID);
    }

    if (this.question.ResultClass.ResultClassType == ChecklistAnswerClassType.Numeric && this.question.UserAnswer.Value) {
      this.question.UserAnswer.Value = toNumber(this.question.UserAnswer.Value);
    }
  }

  public dispatchChange(question: ChecklistsQuestion): void {
    question.isAnswered = isQuestionAnswered(question);
    question.isChecked = false;
    if (question.UserAnswer?.Answer?.RemarksRequired && !question.UserAnswer.Remarks) {
      this.showRemarks = true;
    } else if (question.AttachmentAndRemarksRequiredSettings?.IsRemarkRequired && !question.UserAnswer.Remarks) {
      this.showRemarks = true;
    }
    this.questionChange.emit(question);
  }

  public setRemarks(remarks: string): void {
    if (this.question.UserAnswer.Remarks != remarks) {
      this.question.UserAnswer.Remarks = remarks;
      this.dispatchChange(this.question);
    }
  }

  public setAttachments(attachments: Array<{ id: number, fileName: string, size: number }>): void {
    this.question.UserAnswer.Attachments = attachments.map(attachment => ({
      Id: attachment.id,
      Size: attachment.size,
      FileName: attachment.fileName
    }));
    this.attachments = attachments;
    this.dispatchChange(this.question);
  }

  public toggleQuestionVisibility(): void {
    if (this.isCurrentQuestion) {
      this.question.isCollapsed = !this.question.isCollapsed;
    } else {
      this.question.isCollapsed = false;
    }
    this.collapseQuestion.emit(this.question.isCollapsed);
    // auto-expand remarks when opening the question if required
    if (!this.question.isCollapsed &&
      !this.showRemarks &&
      !this.question.UserAnswer.Remarks &&
      (this.question.AttachmentAndRemarksRequiredSettings?.IsRemarkRequired || this.question.UserAnswer.Answer?.RemarksRequired)) {
      this.showRemarks = true;
    }
  }

  public next(): void {
    this.saveQuestion.emit(this.question);
  }

  public resetButtonsOptions(): void {
    this.showAttachments = false;
    this.showRemarks = false;
  }

  public toggleRemarks(): void {
    this.showRemarks = !this.showRemarks;
  }

  public toggleAttachments(): void {
    this.showAttachments = !this.showAttachments;
  }

  public showPopup(): void {
    if (this.readmorePopup != null) return;
    this.readmorePopup = this.popupService.open({
      anchor: this.anchorEl,
      content: this.readmorePopupTemplateRef,
      popupClass:
        'w-svw h-svh sm:w-fit sm:h-auto sm:min-w-[400px] sm:-mt-[0.5rem] sm:rounded-md maxw-sm:!top-0 !z-[9999] maxw-sm:!left-0 maxw-sm:!right-0',
      animate: false,
      anchorAlign: { horizontal: 'center', vertical: 'bottom' },
      popupAlign: { horizontal: 'center', vertical: 'top' },
      collision: { horizontal: 'fit', vertical: 'fit' }
    });
  }

  public onAnchorMouseEnter(): void {
    this.anchorHovered = true;
    this.showPopup();
  }

  public onAnchorMouseLeave(): void {
    this.anchorHovered = false;
    setTimeout(() => {
      if (!this.anchorHovered && !this.popupHovered) this.hidePopup();
    }, HIDE_POPUP_DELAY_TIME);
  }

  public onPopupMouseEnter(): void {
    this.popupHovered = true;
    this.showPopup();
  }

  public onPopupMouseLeave(): void {
    this.popupHovered = false;
    setTimeout(() => {
      if (!this.anchorHovered && !this.popupHovered) this.hidePopup();
    }, HIDE_POPUP_DELAY_TIME);
  }

  public hidePopup(): void {
    if (this.readmorePopup) {
      this.readmorePopup.close();
      this.readmorePopup = null;
    }
    event?.preventDefault();
  }

  public downloadAttachment(attachment:{id: number, size: number, fileName: string}): void {
    this.checklistApiService.downloadMedia(this.question.VersionsID, attachment.id).subscribe((res)=> {
      if(res.body){
        this.downloadService.startDownload(res.body, attachment.fileName)
      }
    })
  }
}
